import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Edit3, Send, Sparkles } from "lucide-react";

export default function Approvals() {
  const { activeCompanyId } = useAuth();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [edit, setEdit] = useState("");

  const load = async () => {
    const params = { status, ...(activeCompanyId ? { company_id: activeCompanyId } : {}) };
    const { data } = await api.get("/approvals", { params });
    setItems(data);
    if (data.length && !selected) { setSelected(data[0]); setEdit(data[0].draft_text); }
    else if (selected) {
      const upd = data.find((x) => x.id === selected.id);
      if (upd) { setSelected(upd); }
      else { setSelected(data[0] || null); setEdit(data[0]?.draft_text || ""); }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId, status]);

  const decide = async (action) => {
    if (!selected) return;
    try {
      await api.post(`/approvals/${selected.id}/decide`, {
        action,
        edited_text: action === "edit_and_send" ? edit : undefined,
      });
      toast.success(action === "approve" || action === "edit_and_send" ? "Sent" : "Recorded");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="approvals-page">
      <PageHeader
        title="Approval queue"
        subtitle="Review AI-drafted replies before they go out. Every decision is logged."
        actions={
          <div className="flex items-center gap-1">
            {["pending", "sent", "rejected"].map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`px-3 py-1 text-xs font-mono uppercase tracking-widest border rounded-sm ${
                  status === s ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:bg-muted"
                }`}
                data-testid={`approval-filter-${s}`}
              >{s}</button>
            ))}
          </div>
        }
      />
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[380px_1fr] overflow-hidden">
        <div className="border-r border-border overflow-y-auto">
          {items.map((a) => (
            <button
              key={a.id}
              onClick={() => { setSelected(a); setEdit(a.draft_text); }}
              className={`w-full text-left border-b border-border p-3 email-row border-l-2 border-l-transparent ${selected?.id === a.id ? "active" : ""}`}
              data-testid={`approval-row-${a.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium truncate">{a.subject}</div>
                <ConfidenceDot v={a.ai_confidence} />
              </div>
              <div className="text-xs text-muted-foreground truncate">To: {a.recipient}</div>
              <div className="mt-1 text-[11px] font-mono text-muted-foreground">
                {a.reason} · risk {Math.round(a.ai_risk_score * 100)}%
              </div>
            </button>
          ))}
          {items.length === 0 && <div className="p-6 text-muted-foreground text-sm text-center">Nothing here.</div>}
        </div>

        <div className="p-6 overflow-y-auto">
          {selected ? (
            <div className="max-w-3xl">
              <div className="border-l-2 border-terracotta pl-3 mb-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-terracotta flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> AI-drafted reply
                </div>
                <div className="mt-1 font-display text-xl tracking-tight">{selected.subject}</div>
                <div className="mt-1 text-xs text-muted-foreground">To: {selected.recipient}</div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Badge variant="outline" className="rounded-sm text-[10px] font-mono uppercase">Confidence {Math.round(selected.ai_confidence * 100)}%</Badge>
                <Badge variant="outline" className="rounded-sm text-[10px] font-mono uppercase">Risk {Math.round(selected.ai_risk_score * 100)}%</Badge>
                <Badge variant="outline" className="rounded-sm text-[10px] font-mono uppercase text-terracotta border-terracotta">{selected.reason}</Badge>
                {status !== "pending" && <Badge className="rounded-sm text-[10px] font-mono uppercase">{selected.status}</Badge>}
              </div>
              <Textarea
                value={edit}
                onChange={(e) => setEdit(e.target.value)}
                rows={16}
                className="rounded-sm font-mono text-sm"
                disabled={status !== "pending"}
                data-testid="approval-editor"
              />
              {status === "pending" && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button onClick={() => decide("approve")} className="rounded-sm" data-testid="approve-send-button">
                    <Send className="h-4 w-4 mr-1.5" /> Approve & send
                  </Button>
                  <Button onClick={() => decide("edit_and_send")} variant="outline" className="rounded-sm" data-testid="edit-send-button">
                    <Edit3 className="h-4 w-4 mr-1.5" /> Edit & send
                  </Button>
                  <Button onClick={() => decide("revision")} variant="outline" className="rounded-sm" data-testid="revision-button">
                    Return for revision
                  </Button>
                  <Button onClick={() => decide("reject")} variant="destructive" className="rounded-sm" data-testid="reject-button">
                    <XCircle className="h-4 w-4 mr-1.5" /> Reject
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-muted-foreground text-sm">Select an approval to review.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ConfidenceDot({ v }) {
  const color = v >= 0.75 ? "bg-emerald-deep" : v >= 0.5 ? "bg-warning" : "bg-destructive";
  return <div className={`h-2 w-2 rounded-full ${color}`} title={`Confidence ${Math.round(v * 100)}%`} />;
}
